import type { CSSProperties, ReactNode, Ref } from "react";
import { dataEl, type DataElScope } from "@/utils/dataEl";
import { ButtonPreset, TextPreset } from "@/constants/componentPresets";
import { Cbutton } from "../Cbutton";
import { Cmarkdowntext } from "../Cmarkdowntext";
import { Ctext } from "../Ctext";
import { CtextfxReversingDecrypt } from "../Ctextfx";
import { FontWeight } from "@/theme/typography";
import { composeCreferenceLabel } from "./creferenceResolvedEntitySelectors";
import { REFERENCE_PILL_LOADING_PLAY_KEY } from "./creferencePillContentModel";
import { getCreferenceInlineLeadingStyle, getCreferenceInlineRootStyle, getCreferenceInlineTextColor, getCreferenceInlineTextOpacity, getCreferenceInlineTextStyle, } from "./creferenceInlineContentModel";
const REFERENCE_INLINE_LABEL_SIZE = 15;
export interface CreferenceInlineContentProps {
    dataElScope: DataElScope;
    canClick: boolean;
    className?: string;
    disabled: boolean;
    isLoading: boolean;
    isUnresolved: boolean;
    label: string;
    labelSuffix: string | null;
    leading: ReactNode;
    linkedLabelTextStyle: CSSProperties;
    onClick: () => void;
    rootRef: Ref<HTMLSpanElement>;
    style?: CSSProperties;
}
export function CreferenceInlineContent({ dataElScope, canClick, className, disabled, isLoading, isUnresolved, label, labelSuffix, leading, linkedLabelTextStyle, onClick, rootRef, style, }: CreferenceInlineContentProps): ReactNode {
    // Inline mode sets no character budget, so the marker simply joins the
    // label — there is no cut for it to survive.
    const composedLabel = composeCreferenceLabel({ label, labelSuffix });
    return (
    <Cbutton rootAttributes={{ "data-el": dataEl(dataElScope, "InlineContent/Root") }} inline inlineRootRef={rootRef} rootClassName={className} preset={ButtonPreset.Ghost} ariaLabel={canClick ? composedLabel : undefined} padding={0} onClick={canClick ? onClick : undefined} disableHoverEffects={!canClick} style={getCreferenceInlineRootStyle({
            disabled,
            isUnresolved,
            style,
        })} containerStyle={{ display: "inline-flex", alignItems: "center", gap: "5px", flexWrap: "wrap" }}>
      <span style={getCreferenceInlineLeadingStyle()}>
        {leading}
      </span>
      {isLoading ? (
      // Our `[loading]` word, not a name — it breathes rather than sitting
      // there looking like a resolved entity.
      <Ctext preset={TextPreset.Regular} size={REFERENCE_INLINE_LABEL_SIZE} weight={FontWeight.Heavy} color={getCreferenceInlineTextColor(isUnresolved)} opacity={getCreferenceInlineTextOpacity(isUnresolved)}>
        <CtextfxReversingDecrypt dataElScope={dataElScope} text={composedLabel} playKey={REFERENCE_PILL_LOADING_PLAY_KEY}/>
      </Ctext>) : (
      /* Model-authored label — rendered as markdown, bounded by CreferenceDepthBoundary. */
      <Cmarkdowntext dataElScope={dataElScope} content={composedLabel} size={REFERENCE_INLINE_LABEL_SIZE} weight={FontWeight.Heavy} color={getCreferenceInlineTextColor(isUnresolved)} opacity={getCreferenceInlineTextOpacity(isUnresolved)} textStyle={getCreferenceInlineTextStyle({ linkedLabelTextStyle })}/>)}
    </Cbutton>
    );
}
